import { defineStore } from "pinia";
import { ref, watch, computed } from "vue";
import { useGraphStore } from "./graph";
import { InstanceTask } from "@lr-notitia/notitia-operator/schemas";
import { useDomainStore } from "./domain";
import { useSessionStore } from "./alternis/session";
import { useGptSuggestionsStore } from "./gpt/suggestions";
import { useConfigStore } from "./alternis/config";
import { useAlternisNewStore } from "./alternis_new";
import { useInstanceWorkspacesStore } from "./i-workspace";
import type { Message } from "@lr-notitia/notitia-alternis/schemas";
import { apiConfig as operatorAPIConfig } from "@lr-notitia/notitia-operator/client";

export type Questions = {
	pinned: Question[];
	open: Question[];
	closed: Question[];
};
export type Question = {
	title: { [lang: string]: string };
	subtitle: { [lang: string]: string };
	instanceTask: InstanceTask;
	templatePrio?: number;
	noGPTRephrasing?: boolean;
	useSuggestions?: string;
	playButton?: string;
	showUnknownButton?: string;
};

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let sessionStore: ReturnType<typeof useSessionStore>;
let gptSuggestionsStore: ReturnType<typeof useGptSuggestionsStore>;
let configStore: ReturnType<typeof useConfigStore>;
let alternisNewStore: ReturnType<typeof useAlternisNewStore>;
let workspacesStore: ReturnType<typeof useInstanceWorkspacesStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	sessionStore = useSessionStore();
	gptSuggestionsStore = useGptSuggestionsStore();
	configStore = useConfigStore();
	alternisNewStore = useAlternisNewStore();
	workspacesStore = useInstanceWorkspacesStore();
}

export const useQuestionsStore = defineStore("QuestionsStore", () => {
	let eventSource: EventSource | null = null;

	const tab = ref<"open" | "closed">("open");
	const currentQuestion = ref<string | undefined>();
	const zoomToId = ref<string>("");
	const latestAnsweredQuestion = ref<{ dataobjectId: string; index: number }>({
		dataobjectId: "",
		index: -1,
	});
	const suggestionDrag = ref({
		isDragged: false,
		datatypeId: "",
	});
	const soll_umformulieren_counter = ref(0);
	const hideFromOpenQuestions = ref<string[]>([]);

	const pinnedQuestions = ref<Question[]>([]);
	const answeredQuestions = ref<Question[]>([]);
	const allUnansweredQuestions = ref<Question[]>([]);
	const unansweredQuestions = computed<Question[]>(() => {
		return allUnansweredQuestions.value.filter(
			(q) =>
				!hideFromOpenQuestions.value.includes(q.instanceTask.id) &&
				!getTaskTemplate(q.instanceTask.template)?.context?.["metaQuestion"],
		);
	});
	const metaQuestion = computed(() => {
		let allMetaQuestions = allUnansweredQuestions.value.filter(
			(q) =>
				!hideFromOpenQuestions.value.includes(q.instanceTask.id) &&
				getTaskTemplate(q.instanceTask.template)?.context?.["metaQuestion"],
		);
		return allMetaQuestions.length > 0 ? allMetaQuestions[0] : undefined;
	});

	function getTaskTemplate(id: string) {
		if (!graphStore) init();
		return graphStore.template.task.byId(id);
	}

	// for animation
	const answeredTimeouts = new Map();
	const DELAY_DURATION = 3_000;
	function clearAnsweredTimeouts() {
		answeredTimeouts.forEach((timeout) => clearTimeout(timeout));
		answeredTimeouts.clear();
	}

	function initSSE() {
		if (!workspacesStore || !alternisNewStore) init();
		if (!workspacesStore.openedWorkspaceId) return;
		let endpoint = operatorAPIConfig.host;
		endpoint += "/sseQuestions";
		const url = new URL(endpoint, window.location.href);
		url.searchParams.set("domainId", domainStore.domain.id);
		url.searchParams.set("workspaceId", workspacesStore.openedWorkspaceId);
		eventSource = new EventSource(url.toString());

		const currentWorkspaceId = workspacesStore.openedWorkspaceId; // workspaceId at the time of connection

		eventSource.onopen = (e) => {};
		eventSource.onmessage = (e) => {
			// Ignore messages from other workspace sse connections
			if (workspacesStore.openedWorkspaceId !== currentWorkspaceId) {
				clearAnsweredTimeouts();
				return;
			}

			const previousOpenQuestions = allUnansweredQuestions.value; // Temporary: for animation

			let data = JSON.parse(e.data) as Questions;

			data.closed = data.closed.sort((a, b) => {
				if (!a || !b) return 0;
				let prioSort = (b?.templatePrio ?? 0) - (a?.templatePrio ?? 0);
				if (prioSort !== 0) return prioSort;
				let aColor = graphStore.template.task.byId(a.instanceTask.template)?.color || "#000000";
				let bColor = graphStore.template.task.byId(b.instanceTask.template)?.color || "#000000";
				return aColor.localeCompare(bColor);
			}) as Question[];
			data.open = data.open
				.filter((q) => !hideFromOpenQuestions.value.includes(q.instanceTask.id))
				.sort((a, b) => {
					if (!a || !b) return 0;
					let prioSort = (b?.templatePrio ?? 0) - (a?.templatePrio ?? 0);
					if (prioSort !== 0) return prioSort;
					let aColor = graphStore.template.task.byId(a.instanceTask.template)?.color || "#000000";
					let bColor = graphStore.template.task.byId(b.instanceTask.template)?.color || "#000000";
					return aColor.localeCompare(bColor);
				}) as Question[];

			// Temporary: for animation: questions which are now closed but were open previously
			if (alternisNewStore.useNewAlternis && alternisNewStore.isVoicebot) {
				const newClosedQuestions = previousOpenQuestions.filter(
					(q) => !data.open.some((oq) => oq.instanceTask.id === q.instanceTask.id),
				);

				// Temporary: for animation: set a timeout for new closed questions
				newClosedQuestions.forEach((question) => {
					if (!answeredTimeouts.has(question.instanceTask.id)) {
						const timeout = setTimeout(() => {
							allUnansweredQuestions.value = allUnansweredQuestions.value.filter(
								(q) => q.instanceTask.id !== question.instanceTask.id,
							);
							answeredTimeouts.delete(question.instanceTask.id);
						}, DELAY_DURATION);
						answeredTimeouts.set(question.instanceTask.id, timeout);
					}
				});
			}

			pinnedQuestions.value = data.pinned;
			answeredQuestions.value = data.closed;

			// Temporary: for animation: add newClosedQuestions to open questions
			if (alternisNewStore.useNewAlternis && alternisNewStore.isVoicebot) {
				allUnansweredQuestions.value = data.open.concat(
					Array.from(answeredTimeouts.keys())
						.map((id) => previousOpenQuestions.find((q) => q.instanceTask.id === id) as Question)
						.filter(Boolean),
				);
			} else {
				allUnansweredQuestions.value = data.open;
			}
		};
		eventSource.onerror = (e) => {
			console.log("SSE error", e);
		};
	}
	function closeSSE() {
		if (eventSource) eventSource.close();
	}

	function suggestions(newQuestions: Question[]) {
		if (!domainStore || !sessionStore || !gptSuggestionsStore || !alternisNewStore) init();

		// Suggestions for new alternis: server side, not here
		if (alternisNewStore.useNewAlternis) return;

		let chatMessages: Message[] | undefined;
		chatMessages = sessionStore.session?.messages.filter((m) => m.type !== "system").slice(-20);
		if (!chatMessages) return;
		if (chatMessages.length < 1) return;
		if ((chatMessages as Message[]).every((m) => m.userId === domainStore.account.id)) return;
		newQuestions.forEach((newQuestion) => {
			const templateWorkspace = graphStore.template.workspace.byId(
				workspacesStore.openedWorkspace?.template || "",
			);
			if (newQuestion.useSuggestions === "false") return; // individual setting
			if (templateWorkspace?.context?.useSuggestions === false && newQuestion.useSuggestions !== "true") return; // global setting

			if (!chatMessages) return;
			if (chatMessages.length < 1) return;
			gptSuggestionsStore.askLLM(chatMessages, newQuestion);
		});
	}

	watch(
		() => tab.value,
		() => (latestAnsweredQuestion.value = { dataobjectId: "", index: -1 }),
	);

	watch(
		() => answeredQuestions.value,
		(newVal, oldVal) => {
			if (oldVal.length > 0 && newVal.length === 0) {
				tab.value = "open";
			}
		},
	);

	watch(
		() => unansweredQuestions.value,
		(newVal, oldVal) => {
			if (!configStore) init();
			let newQuestions = newVal.filter((q) => !oldVal.some((oq) => oq.instanceTask.id === q.instanceTask.id));
			if (newQuestions && newQuestions.length > 0) suggestions(newQuestions);
		},
	);

	return {
		tab,
		pinnedQuestions,
		answeredQuestions,
		unansweredQuestions,
		metaQuestion,
		currentQuestion,
		zoomToId,
		latestAnsweredQuestion,
		suggestionDrag,
		soll_umformulieren_counter,
		hideFromOpenQuestions,
		initSSE,
		closeSSE,
	};
});
